/**
 * One value drives the icon, its tooltip, and the modal.
 *
 * It reflects the SUBSCRIPTION, not merely Notification.permission: a subscription
 * can die on its own (rotation, a 410 from the push service, storage eviction) while
 * permission stays 'granted'. A status derived from permission alone would read
 * healthy for a device receiving nothing — which is the failure this feature exists
 * to make visible.
 */
export type PushStatus =
  /** granted + live subscription. Working. */
  | 'active'
  /** granted but no subscription. Silently stopped working; one silent tap repairs it. */
  | 'inactive'
  /** permission 'default'. Never asked. */
  | 'off'
  /** permission 'denied'. Nothing in the app can re-prompt. */
  | 'blocked'
  /** No SW/PushManager: an iOS Safari tab, or iOS < 16.4. */
  | 'unsupported'
  /**
   * This server does not do push — an empty VAPID key (`PUSH_ENABLED=false` server-side).
   * Independent of permission state and of `unsupported`, which is a BROWSER limitation with its
   * own Add-to-Home-Screen copy that would be actively misleading here. Not one of the five
   * rendered states: the icon renders nothing at all for it (see push-status-icon), so there is
   * no glyph, no tooltip, and no reachable prompt.
   */
  | 'unavailable';

/** True when this browser can subscribe at all. */
export function isPushSupported(win: Window = window): boolean {
  return 'serviceWorker' in win.navigator && 'PushManager' in win && 'Notification' in win;
}
